import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";
import { PURPLE } from "./ResourceAllocationShared";

/**
 * Assign topics to each plan row — "Day N" for short courses, "Week N" for long courses.
 * These are the topics shown in the public course schedule ("Date & Time | Topic").
 */
export default function PlanTopicsTab({ planId, acdemicCourseId }) {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [editWeek, setEditWeek] = useState(null);
  const [subjectId, setSubjectId] = useState("");
  const [masterTopics, setMasterTopics] = useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadWeeks = useCallback(() => {
    if (!planId) { setWeeks([]); setSubjects([]); return; }
    setLoading(true);
    api.get(`admin/course-plans/${planId}/week-topics`)
      .then(async (r) => {
        setWeeks(r.data?.weeks || []);
        // Prefer the course's own subjects; fall back to the group taxonomy (subject_id / subject_name).
        let list = Array.isArray(r.data?.subjects) ? r.data.subjects : [];
        if (list.length === 0) {
          try {
            const res = await api.get(`admin/course-plans/${planId}/content/subjects`);
            list = res.data?.data || [];
          } catch {
            list = [];
          }
        }
        setSubjects(
          list
            .map((s) => ({ id: s.id ?? s.subject_id, name: s.name ?? s.subject_name }))
            .filter((s) => s.id != null && s.name)
        );
      })
      .catch(() => { setWeeks([]); setSubjects([]); toast.error("Could not load plan topics."); })
      .finally(() => setLoading(false));
  }, [planId]);

  useEffect(() => { loadWeeks(); }, [loadWeeks]);

  useEffect(() => {
    if (!acdemicCourseId || !subjectId) { setMasterTopics([]); return; }
    api.get(`admin/fetch/topics/by-academic-subject/${acdemicCourseId}/${subjectId}`)
      .then((r) => setMasterTopics(r.data?.data || []))
      .catch(() => setMasterTopics([]));
  }, [acdemicCourseId, subjectId]);

  const openEditor = (week) => {
    setEditWeek(week);
    const firstSubject = week.topics?.[0]?.subject_id ?? subjects[0]?.id ?? "";
    setSubjectId(firstSubject ? String(firstSubject) : "");
  };

  // Pre-select what this row already has for the chosen subject.
  useEffect(() => {
    if (!editWeek) return;
    setSelectedTopicIds(
      (editWeek.topics || [])
        .filter((t) => String(t.subject_id) === String(subjectId) && t.topic_id)
        .map((t) => t.topic_id)
    );
  }, [editWeek, subjectId]);

  const topicOptions = useMemo(() => {
    const byId = new Map(masterTopics.map((t) => [Number(t.id), { id: Number(t.id), name: t.name ?? t.title ?? `Topic ${t.id}` }]));
    // Keep already-assigned topics selectable even if the master list no longer returns them.
    (editWeek?.topics || [])
      .filter((t) => String(t.subject_id) === String(subjectId) && t.topic_id && !byId.has(Number(t.topic_id)))
      .forEach((t) => byId.set(Number(t.topic_id), { id: Number(t.topic_id), name: t.name }));
    return Array.from(byId.values());
  }, [masterTopics, editWeek, subjectId]);

  const unitLabel = weeks.some((w) => String(w.label || "").startsWith("Day")) ? "Day" : "Week";

  const save = async () => {
    if (!editWeek || !subjectId) { toast.error("Select a subject."); return; }
    setSaving(true);
    try {
      const r = await api.put(`admin/plan-weeks/${editWeek.id}/topics`, {
        subject_id: Number(subjectId),
        topic_ids: selectedTopicIds,
      });
      const updated = r.data?.week;
      if (updated?.id) {
        setWeeks((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
        setEditWeek(updated);
      }
      toast.success(`${editWeek.label} topics saved.`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save topics.");
    } finally {
      setSaving(false);
    }
  };

  if (!planId) {
    return <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700, py: 2 }}>Select a group to assign topics.</Typography>;
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#374151", mb: 1.5 }}>
        Assign the topics taught on each {unitLabel.toLowerCase()}. They appear in the course schedule on the website.
      </Typography>

      {loading ? (
        <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
      ) : weeks.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700, py: 2 }}>
          This group has no {unitLabel.toLowerCase()}s yet. Set the number of sessions (short course) or plan dates (long course) on the course form.
        </Typography>
      ) : (
        <TableContainer sx={{ border: "1px solid #eef2f7", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f3ff" }}>
                <TableCell sx={{ fontWeight: 700, width: 170 }}>{unitLabel}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Topics</TableCell>
                <TableCell sx={{ width: 90 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {weeks.map((w) => (
                <TableRow key={w.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{w.label}</Typography>
                    <Typography variant="caption" sx={{ color: w.start_date ? "#6b7280" : "#b45309" }}>
                      {w.start_date ? `${w.start_date}${w.end_date && w.end_date !== w.start_date ? ` – ${w.end_date}` : ""}` : "Date TBD"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {w.topics?.length ? (
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                        {w.topics.map((t) => (
                          <Chip key={t.course_topic_id} size="small" label={t.subject_name ? `${t.subject_name}: ${t.name}` : t.name}
                            sx={{ bgcolor: "#ede9fe", color: PURPLE, fontWeight: 600 }} />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No topics assigned</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEditor(w)} sx={{ textTransform: "none", fontWeight: 600, color: PURPLE }}>
                      {w.topics?.length ? "Edit" : "Assign"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(editWeek)} onClose={() => setEditWeek(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editWeek?.label} topics</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Subject"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              fullWidth
              size="small"
              helperText={subjects.length === 0 ? "This course has no subjects yet — add them on the course form." : ""}
            >
              {subjects.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
            </TextField>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={topicOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={topicOptions.filter((o) => selectedTopicIds.includes(o.id))}
              onChange={(_, value) => setSelectedTopicIds(value.map((v) => v.id))}
              disabled={!subjectId}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Topics" placeholder={subjectId ? "Search topics" : "Select a subject first"} />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Saving replaces this subject's topics on {editWeek?.label}. Other subjects on the same {unitLabel.toLowerCase()} are kept.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWeek(null)} sx={{ textTransform: "none" }}>Close</Button>
          <Button variant="contained" onClick={save} disabled={saving || !subjectId}
            sx={{ textTransform: "none", bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE } }}>
            {saving ? "Saving…" : "Save topics"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
